<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
  import { ImageIcon, Play, Video } from "lucide-vue-next";
  import type { MMediumUpload } from "@/types";

  const props = withDefaults(
    defineProps<{
      asset: MMediumUpload;
      playable?: boolean;
      showPlayBadge?: boolean;
      fit?: "cover" | "contain";
    }>(),
    { playable: false, showPlayBadge: false, fit: "cover" },
  );

  const emit = defineEmits<{ measured: [dimensions: { width: number; height: number }] }>();

  const emitDimensions = (event: Event) => {
    const element = event.target as HTMLImageElement | HTMLVideoElement;
    const width = "naturalWidth" in element ? element.naturalWidth : element.videoWidth;
    const height = "naturalHeight" in element ? element.naturalHeight : element.videoHeight;
    if (width && height) emit("measured", { width, height });
  };

  const isVideo = computed(() => props.asset.media_type === "video");
  const fitClass = computed(() => (props.fit === "contain" ? "object-contain" : "object-cover"));

  // The #t=0.1 media fragment forces a seek to the first frame. Chrome paints it without
  // the fragment, Safari does not, and the seek costs ~4% more bytes than metadata alone.
  const videoSrc = computed(() => `${props.asset.url}#t=0.1`);

  // Painting a first frame costs the browser megabytes: it buffers ahead as soon as a
  // video gets a src, whatever `preload` says. So a grid video only gets its src once it
  // is about to be seen — off-screen videos transfer nothing at all.
  const root = ref<HTMLElement | null>(null);
  const isInViewport = ref(false);
  const shouldLoadVideo = computed(() => props.playable || isInViewport.value);

  let observer: IntersectionObserver | null = null;
  const stopObserving = () => {
    observer?.disconnect();
    observer = null;
  };

  onMounted(() => {
    if (!isVideo.value || props.playable) return;
    if (!root.value || typeof IntersectionObserver === "undefined") {
      isInViewport.value = true;
      return;
    }
    observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        isInViewport.value = true;
        stopObserving();
      },
      { rootMargin: "200px" },
    );
    observer.observe(root.value);
  });

  onBeforeUnmount(stopObserving);

  const hasFailed = ref(false);
  watch(
    () => props.asset.url,
    () => (hasFailed.value = false),
  );

  const showsPlaceholder = computed(() => hasFailed.value || (isVideo.value && !shouldLoadVideo.value));
</script>

<template>
  <div
    ref="root"
    class="relative h-full w-full">
    <div
      v-if="showsPlaceholder"
      class="flex h-full w-full flex-col items-center justify-center gap-1 bg-muted p-2 text-muted-foreground">
      <Video
        v-if="isVideo"
        class="h-6 w-6 shrink-0" />
      <ImageIcon
        v-else
        class="h-6 w-6 shrink-0" />
      <span class="w-full truncate text-center text-3xs leading-tight">{{ asset.name }}</span>
    </div>

    <template v-else>
      <video
        v-if="isVideo"
        :src="videoSrc"
        :title="asset.name"
        :controls="playable"
        :class="[fitClass, playable ? '' : 'pointer-events-none']"
        class="h-full w-full bg-black"
        preload="metadata"
        muted
        playsinline
        @loadedmetadata="emitDimensions"
        @error="hasFailed = true" />
      <img
        v-else
        :src="asset.url"
        :alt="asset.name"
        :class="fitClass"
        class="h-full w-full"
        @load="emitDimensions"
        @error="hasFailed = true" />

      <div
        v-if="isVideo && showPlayBadge && !playable"
        class="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span class="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white shadow-sm">
          <Play class="h-4 w-4 translate-x-px fill-current" />
        </span>
      </div>
    </template>
  </div>
</template>
